import { useState, useEffect, useCallback } from 'react';
import type { PortalMenu, PortalCard } from '@me-get/shared';
import { MenuEditor, type MenuFormData } from '../components/MenuEditor';
import { CardEditor, type CardFormData } from '../components/CardEditor';
import { BannerEditor, type BannerFormData } from '../components/BannerEditor';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';
type Tab = 'menus' | 'cards' | 'banners';

interface CompanyOption {
  id: string;
  usual_name?: string;
  usualName?: string;
  client_code?: string;
  clientCode?: string;
}

interface BannerData {
  id: string;
  // Support both camelCase (from API) and snake_case (legacy)
  html_content?: string;
  htmlContent?: string;
  click_action_type?: 'html_embed' | 'external_url' | 'service_link';
  clickActionType?: 'html_embed' | 'external_url' | 'service_link';
  click_action_url?: string | null;
  clickActionUrl?: string | null;
  display_order?: number;
  displayOrder?: number;
  is_active?: boolean;
  isActive?: boolean;
  is_shared?: boolean;
  isShared?: boolean;
}

export function LayoutConfigPage() {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [isSharedMode, setIsSharedMode] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('menus');
  const [menus, setMenus] = useState<PortalMenu[]>([]);
  const [cards, setCards] = useState<PortalCard[]>([]);
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch companies on mount
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const res = await fetch(`${API_BASE}/admin/companies`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load companies');
        const json = await res.json();
        const companiesList = json.data ?? json;
        setCompanies(Array.isArray(companiesList) ? companiesList : []);
        if (Array.isArray(companiesList) && companiesList.length > 0) {
          setSelectedCompanyId(companiesList[0].id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load companies');
      } finally {
        setLoadingCompanies(false);
      }
    }
    fetchCompanies();
  }, []);

  // Fetch menus and cards when company or shared mode changes
  const fetchLayoutData = useCallback(async () => {
    setLoadingData(true);
    setError(null);
    try {
      const basePath = isSharedMode
        ? `${API_BASE}/admin/layout/shared`
        : `${API_BASE}/admin/layout`;

      const menusUrl = isSharedMode
        ? `${basePath}/menus`
        : `${basePath}/menus/${selectedCompanyId}`;
      const cardsUrl = isSharedMode
        ? `${basePath}/cards`
        : `${basePath}/cards/${selectedCompanyId}`;
      const bannersUrl = isSharedMode
        ? `${basePath}/banners`
        : `${basePath}/banners/${selectedCompanyId}`;

      const [menusRes, cardsRes, bannersRes] = await Promise.all([
        fetch(menusUrl, { credentials: 'include' }),
        fetch(cardsUrl, { credentials: 'include' }),
        fetch(bannersUrl, { credentials: 'include' }),
      ]);

      if (!menusRes.ok) throw new Error('Failed to load menus');
      if (!cardsRes.ok) throw new Error('Failed to load cards');
      if (!bannersRes.ok) throw new Error('Failed to load banners');

      const [menusJson, cardsJson, bannersJson] = await Promise.all([
        menusRes.json(),
        cardsRes.json(),
        bannersRes.json(),
      ]);

      setMenus(Array.isArray(menusJson.data ?? menusJson) ? (menusJson.data ?? menusJson) : []);
      setCards(Array.isArray(cardsJson.data ?? cardsJson) ? (cardsJson.data ?? cardsJson) : []);
      setBanners(Array.isArray(bannersJson.data ?? bannersJson) ? (bannersJson.data ?? bannersJson) : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load layout data');
    } finally {
      setLoadingData(false);
    }
  }, [selectedCompanyId, isSharedMode]);

  useEffect(() => {
    if (isSharedMode || selectedCompanyId) {
      fetchLayoutData();
    }
  }, [fetchLayoutData, isSharedMode, selectedCompanyId]);

  // Menu CRUD handlers
  async function handleSaveMenu(data: MenuFormData, id?: string) {
    const basePath = isSharedMode
      ? `${API_BASE}/admin/layout/shared/menus`
      : `${API_BASE}/admin/layout/menus/${selectedCompanyId}`;

    const url = id ? `${basePath}/${id}` : basePath;
    const method = id ? 'PUT' : 'POST';

    const body = {
      label: data.label,
      targetUrl: data.targetUrl,
      displayOrder: data.displayOrder,
      icon: data.icon || null,
      isShared: isSharedMode,
      contentType: data.contentType,
      htmlContent: data.contentType === 'html_embed' ? data.htmlContent : null,
    };

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      throw new Error(errData?.error?.message ?? 'Failed to save menu');
    }

    await fetchLayoutData();
  }

  async function handleDeleteMenu(id: string) {
    const basePath = isSharedMode
      ? `${API_BASE}/admin/layout/shared/menus`
      : `${API_BASE}/admin/layout/menus/${selectedCompanyId}`;

    const res = await fetch(`${basePath}/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      throw new Error(errData?.error?.message ?? 'Failed to delete menu');
    }

    await fetchLayoutData();
  }

  // Card CRUD handlers
  async function handleSaveCard(data: CardFormData, id?: string) {
    const basePath = isSharedMode
      ? `${API_BASE}/admin/layout/shared/cards`
      : `${API_BASE}/admin/layout/cards/${selectedCompanyId}`;

    const url = id ? `${basePath}/${id}` : basePath;
    const method = id ? 'PUT' : 'POST';

    const body = {
      cardNumber: data.cardNumber,
      title: data.title,
      description: data.description,
      contentType: data.contentType,
      htmlContent: data.contentType === 'html' ? data.htmlContent : null,
      linkUrl: data.contentType !== 'html' ? data.linkUrl : null,
      iconOrImage: data.iconOrImage || null,
      isShared: isSharedMode,
      cardSize: data.cardSize,
      textColor: data.textColor || null,
      bgColor: data.bgColor || null,
    };

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      throw new Error(errData?.error?.message ?? 'Failed to save card');
    }

    await fetchLayoutData();
  }

  async function handleDeleteCard(id: string) {
    const basePath = isSharedMode
      ? `${API_BASE}/admin/layout/shared/cards`
      : `${API_BASE}/admin/layout/cards/${selectedCompanyId}`;

    const res = await fetch(`${basePath}/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      throw new Error(errData?.error?.message ?? 'Failed to delete card');
    }

    await fetchLayoutData();
  }

  // Banner CRUD handlers
  async function handleSaveBanner(data: BannerFormData, id?: string) {
    const basePath = isSharedMode
      ? `${API_BASE}/admin/layout/shared/banners`
      : `${API_BASE}/admin/layout/banners/${selectedCompanyId}`;

    const url = id ? `${basePath}/${id}` : basePath;
    const method = id ? 'PUT' : 'POST';

    const body = {
      htmlContent: data.htmlContent,
      clickActionType: data.clickActionType,
      clickActionUrl: data.clickActionUrl || null,
      displayOrder: data.displayOrder,
      isActive: data.isActive,
      isShared: isSharedMode,
    };

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      throw new Error(errData?.error?.message ?? 'Failed to save banner');
    }

    await fetchLayoutData();
  }

  async function handleDeleteBanner(id: string) {
    const basePath = isSharedMode
      ? `${API_BASE}/admin/layout/shared/banners`
      : `${API_BASE}/admin/layout/banners/${selectedCompanyId}`;

    const res = await fetch(`${basePath}/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      throw new Error(errData?.error?.message ?? 'Failed to delete banner');
    }

    await fetchLayoutData();
  }

  function handleCompanyChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setSelectedCompanyId(e.target.value);
    setIsSharedMode(false);
  }

  function handleSharedToggle() {
    setIsSharedMode((prev) => !prev);
  }

  const effectiveCompanyId = isSharedMode ? null : selectedCompanyId;

  return (
    <div className="layout-config-page">
      <div className="page-header">
        <h1>Portal Layout</h1>
        <p>Configure menus and cards for each client company.</p>
      </div>

      {/* Company selector and shared toggle */}
      <div className="layout-config-page__controls">
        <div className="form-group">
          <label htmlFor="company-selector">Company</label>
          <select
            id="company-selector"
            value={selectedCompanyId}
            onChange={handleCompanyChange}
            disabled={loadingCompanies || isSharedMode}
          >
            {loadingCompanies ? (
              <option value="">Loading companies...</option>
            ) : companies.length === 0 ? (
              <option value="">No companies available</option>
            ) : (
              companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.usualName ?? company.usual_name ?? ''} ({company.clientCode ?? company.client_code ?? ''})
                </option>
              ))
            )}
          </select>
        </div>

        <div className="shared-toggle">
          <label htmlFor="shared-mode-toggle" className="toggle-label">
            <input
              id="shared-mode-toggle"
              type="checkbox"
              checked={isSharedMode}
              onChange={handleSharedToggle}
            />
            <span>Shared Area</span>
          </label>
          <span className="toggle-hint">
            {isSharedMode
              ? 'Editing shared menus and cards visible to all clients'
              : 'Editing company-specific layout'}
          </span>
        </div>
      </div>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="layout-config-page__tabs" role="tablist" aria-label="Layout configuration tabs">
        <button
          role="tab"
          aria-selected={activeTab === 'menus'}
          aria-controls="panel-menus"
          className={`tab-btn${activeTab === 'menus' ? ' tab-btn--active' : ''}`}
          onClick={() => setActiveTab('menus')}
        >
          Menus
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'cards'}
          aria-controls="panel-cards"
          className={`tab-btn${activeTab === 'cards' ? ' tab-btn--active' : ''}`}
          onClick={() => setActiveTab('cards')}
        >
          Cards
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'banners'}
          aria-controls="panel-banners"
          className={`tab-btn${activeTab === 'banners' ? ' tab-btn--active' : ''}`}
          onClick={() => setActiveTab('banners')}
        >
          Banners
        </button>
      </div>

      {/* Tab panels */}
      <div className="layout-config-page__panel">
        {activeTab === 'menus' && (
          <div id="panel-menus" role="tabpanel" aria-label="Menus panel">
            <MenuEditor
              menus={menus}
              companyId={effectiveCompanyId}
              isShared={isSharedMode}
              onSave={handleSaveMenu}
              onDelete={handleDeleteMenu}
              loading={loadingData}
            />
          </div>
        )}

        {activeTab === 'cards' && (
          <div id="panel-cards" role="tabpanel" aria-label="Cards panel">
            <CardEditor
              cards={cards}
              companyId={effectiveCompanyId}
              isShared={isSharedMode}
              onSave={handleSaveCard}
              onDelete={handleDeleteCard}
              loading={loadingData}
            />
          </div>
        )}

        {activeTab === 'banners' && (
          <div id="panel-banners" role="tabpanel" aria-label="Banners panel">
            <BannerEditor
              banners={banners}
              companyId={effectiveCompanyId}
              isShared={isSharedMode}
              onSave={handleSaveBanner}
              onDelete={handleDeleteBanner}
              loading={loadingData}
            />
          </div>
        )}
      </div>
    </div>
  );
}
